import React, { useEffect, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout";
import logo from "../../assets/img/logo-full.png";
import authImage from "../../assets/img/auth/auth.png";
import { useForm } from "react-hook-form";
import { get, post } from "../../services/api";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

function Login({ setIsLoggedIn }) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const navigate = useNavigate();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ---- Page loader state (1.5 seconds seekbar with logo) ----
  const [pageLoading, setPageLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // start incremental progress for 1.5 seconds total
    const stepMs = 30; // interval in ms
    const totalMs = 1500; // 1.5 seconds
    const steps = Math.ceil(totalMs / stepMs);
    const increment = 100 / steps;

    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      setProgress((p) => {
        const next = Math.min(
          100,
          Math.round((current + Number.EPSILON) * 100) / 100
        );
        return next;
      });
    }, stepMs);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      setProgress(100);
      setPageLoading(false);
    }, totalMs);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // ==== LOGIN HANDLER ====
  const saveData = async (data) => {
    try {
      const formData = {
        ...data,
        userType: "Admin",
      };

      const response = await post("Auth/Login", formData);

      localStorage.setItem("token", response.token.tokenString);
      localStorage.setItem("tokenExpiration", response.token.expirationTime);
      localStorage.setItem("loggedIn", true);
      localStorage.setItem("organizationId", response.organizationId);
      localStorage.setItem("username", response.userName);
      localStorage.setItem("userId", response.id);
      localStorage.setItem("roleId", response.roleId);
      localStorage.setItem("accountLength", response.accountLength);
      localStorage.setItem("centerId", response.centerId);
      localStorage.setItem("departmentId", response.departmentId);
      localStorage.setItem("centerUserId", response.centerUserId);
      localStorage.setItem("accountString", response.accountString);

      localStorage.setItem(
        "organizationLogoPath",
        response.organizationLogoPath
      );
      localStorage.setItem("centerLogoPath", response.centerLogoPath);

      setIsLoggedIn(true);
      navigate("/");
    } catch (error) {
      if (error) {
        toast.error(error.response?.statusText || "Login error");
      }
      console.error("Login Error.", error?.response || error);
    }
  };

  // ==== FORGOT PASSWORD HANDLER ====
  const handleForgotPassword = async (data) => {
    try {
      const response = await get(
        `UserAuthentication/ForgetPassword?userOrEmail=${data.email}`
      );

      if (response?.isSuccess) {
        toast.success(response?.message);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // overlay style for submission loader (inside card)
  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    borderRadius: 12,
  };

  // page loader overlay (full screen)
  const pageLoaderStyle = {
    position: "fixed",
    inset: 0,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 11000,
    flexDirection: "column",
  };

  return (
    <>
      {/* PAGE-LOAD SEEKBAR (shows for first 1.5 seconds when page mounts) */}
      {pageLoading && (
        <div style={pageLoaderStyle} aria-hidden={!pageLoading}>
          <div style={{ textAlign: "center" }}>
            <img
              src={logo}
              alt="Inspirer logo"
              style={{ width: 160, height: "auto" }}
            />
            <div
              style={{
                marginTop: 20,
                width: 360,
                height: 10,
                background: "#eef2f7",
                borderRadius: 6,
                overflow: "hidden",
                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  background: "#f59e0b",
                  transition: "width 30ms linear",
                }}
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
                role="progressbar"
              />
            </div>
          </div>
        </div>
      )}

      <AuthLayout>
        <div className="row flex-center position-relative min-vh-100 g-0 py-5">
          <div className="col-11 col-sm-10 col-xl-10">
            {/* card => make relative so overlay can cover it */}
            <div
              className="card border border-translucent auth-card"
              style={{ position: "relative" }}
            >
              {/* Loader overlay (shows during form submission) */}
              {isSubmitting && (
                <div style={overlayStyle} aria-live="polite" aria-busy="true">
                  <div className="text-center">
                    <div
                      className="spinner-border"
                      role="status"
                      style={{ width: 48, height: 48 }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div
                      style={{
                        marginTop: 10,
                        fontWeight: 600,
                        color: "#6b7280",
                      }}
                    >
                      Please wait...
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body pe-md-0">
                <div className="row gx-0 gy-0 align-items-stretch">
                  {/* LEFT: IMAGE */}
                  <div
                    className="col-md-6 d-none d-md-flex align-items-center justify-content-center"
                    style={{
                      background: "#f7f8fa",
                      borderTopLeftRadius: 12,
                      borderBottomLeftRadius: 12,
                      overflow: "hidden",
                      minHeight: 420,
                      padding: 0,
                    }}
                  >
                    <img
                      src={authImage}
                      alt="Auth visual"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain", // full image visible, no crop
                        backgroundColor: "#690505ff",
                      }}
                    />
                  </div>

                  {/* RIGHT: LOGIN FORM */}
                  <div className="col-12 col-md-6 d-flex align-items-center">
                    <div className="w-100 p-4 p-md-5">
                      <div className="text-center mb-4">
                        <div className="d-flex justify-content-center mb-3">
                          <img src={logo} alt="Logo" width="200" />
                        </div>
                        <h2 className="mb-0 fw-bold">Please login!</h2>
                        <p className="text-muted">
                          Welcome Back, Please login to your account.
                        </p>
                      </div>

                      <form
                        onSubmit={handleSubmit(
                          showForgotPassword ? handleForgotPassword : saveData
                        )}
                        className="row g-3"
                      >
                        {!showForgotPassword ? (
                          <>
                            <div className="col-12">
                              <label className="form-label form-label-new">
                                Username <span className="text-danger">*</span>
                              </label>
                              <input
                                {...register("userName", {
                                  required: "This field is required.",
                                })}
                                type="text"
                                className="form-control"
                                placeholder="Username"
                              />
                              {errors.userName && (
                                <small className="text-danger">
                                  {errors.userName.message}
                                </small>
                              )}
                            </div>

                            <div className="col-12">
                              <label className="form-label form-label-new">
                                Password <span className="text-danger">*</span>
                              </label>
                              <div className="position-relative">
                                <input
                                  {...register("passwordHash", {
                                    required: "This field is required.",
                                  })}
                                  type={showPassword ? "text" : "password"}
                                  className="form-control pe-5"
                                  placeholder="Password"
                                />
                                <span
                                  onClick={() => setShowPassword(!showPassword)}
                                  style={{
                                    position: "absolute",
                                    right: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    color: "#6b7280",
                                  }}
                                  aria-label={
                                    showPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ")
                                      setShowPassword(!showPassword);
                                  }}
                                >
                                  <i
                                    className={`fas ${
                                      showPassword ? "fa-eye-slash" : "fa-eye"
                                    }`}
                                  ></i>
                                </span>
                              </div>
                              {errors.passwordHash && (
                                <small className="text-danger">
                                  {errors.passwordHash.message}
                                </small>
                              )}
                            </div>

                            <div className="col-12 mb-3">
                              <div className="row align-items-center">
                                <div className="col-auto">
                                  <div className="form-check mb-0">
                                    <input
                                      className="form-check-input"
                                      id="basic-checkbox"
                                      type="checkbox"
                                      {...register("remember", {})}
                                    />
                                    <label
                                      className="form-check-label mb-0"
                                      htmlFor="basic-checkbox"
                                    >
                                      Remember me
                                    </label>
                                  </div>
                                </div>
                                <div className="col text-end">
                                  <button
                                    type="button"
                                    className="fs-9 fw-semibold btn btn-link p-0"
                                    onClick={() => setShowForgotPassword(true)}
                                  >
                                    Forgot Password?
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="col-12">
                              <button
                                className="btn w-100 mb-1"
                                style={{
                                  background: "#f59e0b",
                                  borderColor: "#f59e0b",
                                  color: "#fff",
                                  padding: "12px 18px",
                                  fontWeight: 700,
                                  boxShadow:
                                    "0 10px 20px rgba(245,158,11,0.12)",
                                  borderRadius: 8,
                                }}
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <span
                                    className="spinner-border spinner-custom-size me-1"
                                    aria-hidden="true"
                                  />
                                ) : null}
                                <span role="status">Login</span>
                              </button>

                              {/* <div className="text-center mt-2">
                                <NavLink
                                  className="fs-9 fw-bold"
                                  to={"/register"}
                                >
                                  Register
                                </NavLink>
                              </div> */}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="col-12">
                              <label className="form-label form-label-new">
                                Email Or Username{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                {...register("email", {
                                  required: "This field is required.",
                                })}
                                type="text"
                                className="form-control"
                                placeholder="Email Or Username"
                              />
                              {errors.email && (
                                <small className="text-danger">
                                  {errors.email.message}
                                </small>
                              )}
                            </div>
                            <div className="col-12">
                              <button
                                className="btn w-100 mb-1"
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                  background: "#f59e0b",
                                  borderColor: "#f59e0b",
                                  color: "#fff",
                                  padding: "12px 18px",
                                  fontWeight: 700,
                                  borderRadius: 8,
                                }}
                              >
                                {isSubmitting ? (
                                  <span
                                    className="spinner-border spinner-custom-size me-1"
                                    aria-hidden="true"
                                  />
                                ) : null}
                                <span role="status">
                                  Send Reset Password Link
                                </span>
                              </button>
                              <button
                                type="button"
                                className="btn btn-link w-100"
                                onClick={() => setShowForgotPassword(false)}
                              >
                                Back to Login
                              </button>
                            </div>
                          </>
                        )}
                      </form>

                      <div
                        className="text-center mt-4"
                        style={{ fontSize: 12, color: "#6b7280" }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          Manage Your School SMARTLY
                        </div>
                        <div>Email: info@inspirer.com.pk</div>
                        <div>
                          <a
                            href="https://www.inspirer.com.pk"
                            target="_blank"
                            rel="noreferrer"
                          >
                            www.inspirer.com.pk
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* END RIGHT */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    </>
  );
}

export default Login;
